import { useStore } from 'zustand';
import { createStore } from 'zustand/vanilla';
import type { GeneralUser, GeneralUserAccessInfo } from '@mews-commander/data-access';
import { AppError } from '@mews/framework';

type UpdateUserParams = Pick<Partial<GeneralUser>, 'authenticator' | 'hasUnlockPin' | 'imageId'>;
type SafeGeneralUser = GeneralUser & Pick<GeneralUserAccessInfo, 'isSecure'>;

interface CurrentUserStore {
    user: SafeGeneralUser | null;
    setUser: (user: SafeGeneralUser) => void;
    updateUser: (user: UpdateUserParams) => void;
}

const currentUserStore = createStore<CurrentUserStore>((set) => ({
    user: null,
    setUser: (user) => set({ user }),
    updateUser: (user) =>
        set((state) => {
            if (state.user === null) throw new AppError('Current user not set');
            return { user: { ...state.user, ...user } };
        }),
}));

export const setCurrentUser = currentUserStore.getState().setUser;
export const updateCurrentUser = currentUserStore.getState().updateUser;

export const useCurrentUser = () => {
    const user = useStore(currentUserStore, (store) => store.user);
    if (user === null) throw new AppError('Current user not set');
    return user;
};

/**
 * @deprecated use useCurrentUser instead.
 * Only use in special cases outside of react, e.g. while migrating legacy redux selectors.
 * It doesn't return a reactive value, so for safety reasons, use as `() => getCurrentUser()` in createSelector.
 * */
export const getCurrentUser = () => {
    const { user } = currentUserStore.getState();
    if (user === null) throw new AppError('Current user not set');
    return user;
};

/**
 * @deprecated use useCurrentUser instead.
 * This function was created temporally because its called when the User is not logged in.
 * It should be refactored in NewRelic logging.
 * We should not be accessing the currentUser when we are not logged in.
 * */
export const getCurrentUserOrNull = () => currentUserStore.getState().user;
